import { Field, User } from '../models';

export function hasGridsPageRole(user?: User): boolean {
  if (isSuperadmin(user)) return true;
  const permissions = getEffectiveAllowTargets(user);
  return permissions.length > 0 && permissions.some(m => m.startsWith('dataSets.'));
}

export function hasTasksPageRole(user?: User): boolean {
  if (isSuperadmin(user)) return true;
  const permissions = getEffectiveAllowTargets(user);
  return permissions.length > 0 && permissions.some(m => m.includes('.tasks.') || m.includes('.tasks.evaluate'));
}

export function hasReportsPageRole(user?: User): boolean {
  if (isSuperadmin(user)) return true;
  return hasEffectivePermission(user, ['reports.view']);
}

export function hasOnlineUsersPageRole(user?: User): boolean {
  if (isSuperadmin(user)) return true;
  return hasEffectivePermission(user, ['onlineUsers.view']);
}

export function hasAdminPageRole(user?: User): boolean {
  if (isSuperadmin(user)) return true;
  return (
    hasAdminUsersPageRole(user) ||
    hasAdminUserGroupsPageRole(user) ||
    hasAdminRolesPageRole(user) ||
    hasAdminGridsPageRole(user) ||
    hasAdminFiltersPageRole(user) ||
    hasAdminIntegrationsPageRole(user) ||
    hasAdminFailedQueueMessagesPageRole(user) ||
    hasAdminBackupsPageRole(user) ||
    hasAdminCachesPageRole(user) ||
    hasAdminLogsPageRole(user) ||
    hasAdminDictionaryPageRole(user) ||
    hasAdminDelegationsPageRole(user) ||
    hasAdminSystemSettingsPageRole(user)
  );
}

export function hasAdminUsersPageRole(user?: User): boolean {
  if (isSuperadmin(user)) return true;
  const permissions = getEffectiveAllowTargets(user);
  return permissions.length > 0 && permissions.some(m => m.startsWith('users.'));
}

export function hasAdminUserGroupsPageRole(user?: User): boolean {
  if (isSuperadmin(user)) return true;
  const permissions = getEffectiveAllowTargets(user);
  return permissions.length > 0 && permissions.some(m => m.startsWith('userGroups.'));
}

export function hasAdminRolesPageRole(user?: User): boolean {
  if (isSuperadmin(user)) return true;
  const permissions = getEffectiveAllowTargets(user);
  return permissions.length > 0 && permissions.some(m => m.startsWith('authorization.'));
}

export function hasAdminGridsPageRole(user?: User): boolean {
  if (isSuperadmin(user)) return true;
  const permissions = getEffectiveAllowTargets(user);
  return (
    permissions.length > 0 &&
    permissions.some(
      m => m.startsWith('dataSets.') && (m.includes('.view') || m.includes('.create') || m.includes('.edit') || m.includes('.delete')),
    )
  );
}

export function hasAdminIntegrationsPageRole(user?: User): boolean {
  if (isSuperadmin(user)) return true;
  const permissions = getEffectiveAllowTargets(user);
  return permissions.length > 0 && permissions.some(m => m.startsWith('integrations.') && m.split('.').length <= 2);
}

export function hasAdminSystemSettingsPageRole(user?: User): boolean {
  if (isSuperadmin(user)) return true;
  const permissions = getEffectiveAllowTargets(user);
  return permissions.length > 0 && permissions.some(m => m.startsWith('systemSettings.'));
}

export function hasAdminCachesPageRole(user?: User): boolean {
  if (isSuperadmin(user)) return true;
  const permissions = getEffectiveAllowTargets(user);
  return permissions.length > 0 && permissions.some(m => m.startsWith('caches.'));
}

export function hasAdminFiltersPageRole(user?: User): boolean {
  if (isSuperadmin(user)) return true;
  const permissions = getEffectiveAllowTargets(user);
  return permissions.length > 0 && permissions.some(m => m.startsWith('filters.'));
}

export function hasAdminBackupsPageRole(user?: User): boolean {
  if (isSuperadmin(user)) return true;
  const permissions = getEffectiveAllowTargets(user);
  return permissions.length > 0 && permissions.some(m => m.startsWith('backups.'));
}

export function hasAdminLogsPageRole(user?: User): boolean {
  if (isSuperadmin(user)) return true;
  const permissions = getEffectiveAllowTargets(user);
  return permissions.length > 0 && permissions.some(m => m.startsWith('logs.'));
}

export function hasAdminDictionaryPageRole(user?: User): boolean {
  if (isSuperadmin(user)) return true;
  const permissions = getEffectiveAllowTargets(user);
  return permissions.length > 0 && permissions.some(m => m.startsWith('dictionary.'));
}

export function hasAdminDelegationsPageRole(user?: User): boolean {
  if (isSuperadmin(user)) return true;
  const permissions = getEffectiveAllowTargets(user);
  return permissions.length > 0 && permissions.some(m => m.startsWith('delegations.'));
}

export function hasAdminFailedQueueMessagesPageRole(user?: User): boolean {
  if (isSuperadmin(user)) return true;
  const permissions = getEffectiveAllowTargets(user);
  return permissions.length > 0 && permissions.some(m => m.startsWith('queue.'));
}

export function isSuperadmin(user?: User): boolean {
  if (!user || !user.roles) {
    return false;
  }
  return user.roles.some(m => m.name.toLowerCase() === 'superadmin');
}

export function hasIgnoreMaintenancePermission(user?: User): boolean {
  if (isSuperadmin(user)) return true;
  return hasEffectivePermission(user, ['systemMaintenance.ignore']);
}

export function hasSetWorkflowStatusPermission(user?: User, field?: Field): boolean {
  if (isSuperadmin(user)) return true;
  const fieldId = field?.id;
  const gridId = field?.gridId;

  if (!fieldId || !gridId) return false;

  return hasEffectivePermission(user, [
    `dataSets.${gridId}.dataElements.${fieldId}.tasks.setWorkflowStatus`,
    `dataSets.${gridId}.dataElements.*.tasks.setWorkflowStatus`,
    `dataSets.*.dataElements.*.tasks.setWorkflowStatus`,
  ]);
}

export function hasViewProcessingRecordsPermission(user?: User, gridId?: string): boolean {
  if (isSuperadmin(user)) return true;
  const targets = [`dataSets.*.records.viewProcessing`];
  if (gridId) {
    targets.push(`dataSets.${gridId}.records.viewProcessing`);
  }
  return hasEffectivePermission(user, targets);
}

export function hasEditReadOnlyPermission(user?: User, gridId?: string): boolean {
  if (isSuperadmin(user)) return true;
  const targets = [`dataSets.*.records.editReadOnly`];
  if (gridId) {
    targets.push(`dataSets.${gridId}.records.editReadOnly`);
  }
  return hasEffectivePermission(user, targets);
}

export function hasEditInactivePermission(user?: User, gridId?: string): boolean {
  if (isSuperadmin(user)) return true;
  const targets = [`dataSets.*.records.inactivate`];
  if (gridId) {
    targets.push(`dataSets.${gridId}.records.inactivate`);
  }
  return hasEffectivePermission(user, targets);
}

export function hasCreateRecordPermission(user?: User, gridId?: string): boolean {
  if (isSuperadmin(user)) return true;
  const targets = [`dataSets.*.records.create`];
  if (gridId) {
    targets.push(`dataSets.${gridId}.records.create`);
  }
  return hasEffectivePermission(user, targets);
}

export function hasExportToExcelPermission(user?: User, gridId?: string): boolean {
  if (isSuperadmin(user)) return true;
  const targets = [`dataSets.*.records.exportToExcel`];
  if (gridId) {
    targets.push(`dataSets.${gridId}.records.exportToExcel`);
  }
  return hasEffectivePermission(user, targets);
}

export function hasCopyRecordPermission(user?: User, gridId?: string): boolean {
  if (isSuperadmin(user)) return true;
  const targets = [`dataSets.*.records.copy`];
  if (gridId) {
    targets.push(`dataSets.${gridId}.records.copy`);
  }
  return hasEffectivePermission(user, targets);
}

export function hasBumpRevisionPermission(user?: User, gridId?: string): boolean {
  if (isSuperadmin(user)) return true;
  const targets = [`dataSets.*.records.bumpRevision`];
  if (gridId) {
    targets.push(`dataSets.${gridId}.records.bumpRevision`);
  }
  return hasEffectivePermission(user, targets);
}

export function getFieldIdsUserCanEditValue(user?: User, gridId?: string) {
  if (isSuperadmin(user)) return ['*'];
  const permissions = getEffectiveAllowTargets(user);
  if (!permissions || permissions.length === 0) {
    return [];
  }

  // Check for wildcard permissions (all fields)
  const hasWildcardPermission = permissions.some(
    m =>
      m === `dataSets.${gridId}.dataElements.*.tasks.create` ||
      m === `dataSets.${gridId}.dataElements.*.tasks.evaluate` ||
      m === `dataSets.${gridId}.dataElements.*.tasks.setWorkflowStatus` ||
      m === `dataSets.*.dataElements.*.tasks.create` ||
      m === `dataSets.*.dataElements.*.tasks.evaluate` ||
      m === `dataSets.*.dataElements.*.tasks.setWorkflowStatus`,
  );

  if (hasWildcardPermission) {
    return ['*'];
  }

  // Extract specific field IDs from permissions
  const fieldIds = new Set<string>();
  const regex = new RegExp(`dataSets\\.(?:${gridId}|\\*)?\\.dataElements\\.([a-f0-9-]+)\\.tasks\\.(?:create|evaluate|setWorkflowStatus)`);

  for (const permission of permissions) {
    const match = permission.match(regex);
    if (match && match[1]) {
      fieldIds.add(match[1]);
    }
  }

  return Array.from(fieldIds);
}

export function hasPermission(user?: User, permissionKey?: string): boolean {
  if (!permissionKey || permissionKey.trim() === '') return false;
  if (isSuperadmin(user)) return true;
  return hasEffectivePermission(user, [permissionKey]);
}

function getPermissionsByEffect(user: User | undefined, effect: 'ALLOW' | 'DENY'): string[] {
  if (!user?.permissions2?.length) {
    return [];
  }

  return user.permissions2.filter(p => p.effect === effect).map(p => p.target);
}

function getDenyTargetsWithoutFilterGroup(user: User | undefined): string[] {
  if (!user?.permissions2?.length) {
    return [];
  }

  return user.permissions2.filter(p => p.effect === 'DENY' && !p.filterGroup).map(p => p.target);
}

function getEffectiveAllowTargets(user?: User): string[] {
  const allowTargets = getPermissionsByEffect(user, 'ALLOW');
  if (allowTargets.length === 0) {
    return [];
  }

  const denyTargets = getDenyTargetsWithoutFilterGroup(user);
  if (denyTargets.length === 0) {
    return allowTargets;
  }

  return allowTargets.filter(target => !isTargetDenied(target, denyTargets));
}

function hasEffectivePermission(user: User | undefined, targetKeys: string[]): boolean {
  if (!user || targetKeys.length === 0) {
    return false;
  }

  const allowTargets = getPermissionsByEffect(user, 'ALLOW');
  if (allowTargets.length === 0) {
    return false;
  }

  const denyTargets = getDenyTargetsWithoutFilterGroup(user);
  const hasDeniedTarget = targetKeys.some(target => isTargetDenied(target, denyTargets));
  if (hasDeniedTarget) {
    return false;
  }

  return targetKeys.some(target => allowTargets.some(allowTarget => isTargetMatch(target, allowTarget)));
}

function isTargetDenied(target: string, denyTargets: string[]): boolean {
  if (denyTargets.length === 0) return false;
  return denyTargets.some(denyTarget => isTargetMatch(target, denyTarget));
}

function isTargetMatch(target: string, pattern: string): boolean {
  if (!pattern.includes('*')) {
    return target === pattern;
  }

  const regex = createTargetRegex(pattern);
  return regex.test(target);
}

function createTargetRegex(pattern: string): RegExp {
  const escaped = pattern
    .split('.')
    .map(segment => (segment === '*' ? '[^.]+' : escapeRegex(segment)))
    .join('\\.');

  return new RegExp(`^${escaped}$`);
}

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
